import { ReportModel } from '@/models/report';
import { MeasureModel } from '@/models/measure';
import { DimensionModel } from '@/models/dimension';
import { FilterColumnList } from '@/collections/filter_columns';
import { FilterColumnModel } from '@/models/filter_column';
import { ReorderSettingParam } from '@/models/reorder_setting_param';

interface ScopeFilter {
  id: number | null;
  operator: 'eq' | 'ne' | 'all' | 'null';
  scope: 'supplier' | 'location';
}

interface ReportColumnConfig {
  code: string;
  dataType: string;
  name: string;
  key?: string; // Add unique key for v-data-table
}

interface PaginationOptions {
  page: number;
  itemsPerPage: number;
  sortBy: string[];
  sortDesc: boolean[];
}

interface TableOptions extends PaginationOptions {
  search?: string;
  filters?: FilterColumnList;
}

interface SettingScope {
    product: string;
    supplier: string;
    parameter: string;
    location: string;
  }

export class ReplenishmentSettingHelper {

  /**
   * Get global parameters for replenishment settings
   * @param supplierScope - The scope of supplier ('global' | 'specific')
   * @param supplierId - The supplier ID (required if supplierScope is 'specific')
   * @returns Promise<ReorderSettingParam>
   */
  public static async getGlobalParams(settingScope: SettingScope, id?: number): Promise<ReorderSettingParam> {
    try {
      const report = this.setupReport(settingScope);
      const filterColumns = new FilterColumnList();

      if (settingScope.supplier === 'specific' && id) {
        const filterColumn = new FilterColumnModel();
        filterColumn.code = 'supplier_id';
        filterColumn.value = [id.toString()];
        filterColumn.operator = 'eq';
        filterColumn.dataType = 'number';
        filterColumns.add(filterColumn);
      } else if (settingScope.location === 'specific' && id) {
        const filterColumn = new FilterColumnModel();
        filterColumn.code = 'location_id';
        filterColumn.value = [id.toString()];
        filterColumn.operator = 'eq';
        filterColumn.dataType = 'number';
        filterColumns.add(filterColumn);
      }

      report.filterColumns = filterColumns;
      await report.getReport(false, false);

      const rows = report.getReportRowsData() || [];
      const param = new ReorderSettingParam();
      if (rows.length > 0) {
        param.mapData(rows[0]);
      }
      return param;
    } catch (error) {
      console.error('Error fetching global parameters:', error);
      throw error;
    }
  }

  /**
   * Fetch paginated products with replenishment settings
   */
  public static async fetchProductsWithPagination(
    options: TableOptions,
    scopeFilter?: ScopeFilter,
  ): Promise<{ items: any[]; total: number }> {
    try {
      const report = new ReportModel();
      report.measure = new MeasureModel({ code: this.MEASURE_CODE });
      report.dimension = new DimensionModel({ code: 'by_product' });
      report.isDetail = true;
      report.isMetric = false;

      // Apply pagination
      report.page = options.page;
      report.perPage = options.itemsPerPage;

      // Apply sorting
      if (options.sortBy.length > 0) {
        report.sortBy = options.sortBy;
        report.sortDesc = options.sortDesc;
      }

      // Set report columns with unique keys
      const reportColumns = [
        { code: 'product_id', dataType: 'text', name: 'Product Id', key: 'product_id' },
        { code: 'Variant Name', dataType: 'text', name: 'Product Variant', key: 'variant_name' },
        { code: 'Item[Item Description]', dataType: 'text', name: 'Product', key: 'product_name' },
        ...this.getBaseColumns(),
      ];

      report.reportColumns = reportColumns;

      // Handle filters
      const filterColumns = new FilterColumnList();
      if (options.filters) {
        for (const filterColumn of options.filters.items) {
          filterColumns.add(filterColumn);
        }
      }

      // Handle supplier filter
      this.handleScopeFilter(filterColumns, scopeFilter);
      report.filterColumns = filterColumns;

      // Get total count first
      const totalReport = new ReportModel();
      totalReport.measure = report.measure;
      totalReport.dimension = report.dimension;
      totalReport.isDetail = report.isDetail;
      totalReport.isMetric = report.isMetric;
      totalReport.reportColumns = report.reportColumns;
      totalReport.filterColumns = report.filterColumns;
      totalReport.page = -1;


      // Get paginated data
      await report.getReport(false);
      const items = report.getReportRowsData();

      const countData = await totalReport.fetchCount(false) as any;
      const total = parseInt(countData?.total || '0', 10);

      return {
        items: items || [],
        total,
      };
    } catch (error) {
      console.error('Error fetching products:', error);
      throw error;
    }
  }

  /**
   * Fetch paginated variants with replenishment settings
   */
  public static async fetchVariantsWithPagination(
    options: TableOptions,
    scopeFilter?: ScopeFilter,
  ): Promise<{ items: any[]; total: number }> {
    try {
      const report = new ReportModel();
      report.measure = new MeasureModel({ code: this.MEASURE_CODE });
      report.dimension = new DimensionModel({ code: 'by_variant' });
      report.isDetail = true;
      report.isMetric = false;

      // Apply pagination
      report.page = options.page;
      report.perPage = options.itemsPerPage;

      // Apply sorting
      if (options.sortBy.length > 0) {
        report.sortBy = options.sortBy;
        report.sortDesc = options.sortDesc;
      }

      // Set report columns with unique keys
      const reportColumns = [
        { code: 'variant_id', dataType: 'text', name: 'Variant Id', key: 'variant_id' },
        { code: 'product_id', dataType: 'text', name: 'Product Id', key: 'product_id' },
        { code: 'Variant Name', dataType: 'text', name: 'Product Variant', key: 'variant_name' },
        { code: 'Item[Item Description]', dataType: 'text', name: 'Product', key: 'product_name' },
        { code: 'variant', dataType: 'text', name: 'Variant', key: 'variant' },
        { code: 'product_sku', dataType: 'text', name: 'SKU', key: 'sku' },
        { code: 'product_barcode', dataType: 'text', name: 'Barcode', key: 'barcode' },
        ...this.getBaseColumns(),
      ];

      report.reportColumns = reportColumns;

      // Handle filters
      const filterColumns = new FilterColumnList();
      if (options.filters) {
        for (const filterColumn of options.filters.items) {
          filterColumns.add(filterColumn);
        }
      }

      // Handle supplier filter
      this.handleScopeFilter(filterColumns, scopeFilter);
      report.filterColumns = filterColumns;

      // Get total count first
      const totalReport = new ReportModel();
      totalReport.measure = report.measure;
      totalReport.dimension = report.dimension;
      totalReport.isDetail = report.isDetail;
      totalReport.isMetric = report.isMetric;
      totalReport.reportColumns = report.reportColumns;
      totalReport.filterColumns = report.filterColumns;
      totalReport.page = -1;


      // Get paginated data
      await report.getReport(false);
      const items = report.getReportRowsData();

      const countData = await totalReport.fetchCount(false) as any;
      const total = parseInt(countData?.total || '0', 10);

      return {
        items: items || [],
        total,
      };
    } catch (error) {
      console.error('Error fetching variants:', error);
      throw error;
    }
  }
  private static readonly MEASURE_CODE = 'replenishment_settings';

  private static getBaseColumns(): ReportColumnConfig[] {
    return [
      // { code: 'supplier', dataType: 'text', name: 'Supplier', key: 'supplier' },
      // { code: 'supplier_id', dataType: 'text', name: 'Supplier Id', key: 'supplier_id' },
      { code: 'Vendor Name', dataType: 'text', name: 'Vendor', key: 'vendor_name' },
      { code: '[vendor_lead_time]', dataType: 'decimal', name: 'Vendor Lead Time', key: 'lead_time' },
      { code: '[min_order_quantity]', dataType: 'decimal', name: 'Min Order Quantity', key: 'min_order_qty' },
      { code: '[max_order_quantity]', dataType: 'decimal', name: 'Max Order Quantity', key: 'max_order_qty' },
      { code: '[fixed_order_quantity]', dataType: 'decimal', name: 'Fixed Order Quantity', key: 'fixed_order_qty' },
      { code: '[reorder_cycle]', dataType: 'number', name: 'Reorder Cycle', key: 'reorder_cycle' },
      { code: '[replenishment_exclusion]', dataType: 'number', name: 'Replenishment Exclusion', key: 'replenishment_exclusion' },
    ];
  }

  private static setupReport(settingScope: SettingScope): ReportModel {
    const report = new ReportModel();
    report.measure = new MeasureModel({ code: this.MEASURE_CODE });
    if (settingScope.parameter == 'specific') {
      if (settingScope.product == 'product') {
        report.dimension = new DimensionModel({ code: 'by_product' });
      } else {
        report.dimension = new DimensionModel({ code: 'by_variant' });
      }
    }
    report.isDetail = true;
    report.isMetric = false;
    report.page = 1;
    report.perPage = 100;
    return report;
  }

  private static handleScopeFilter(filterColumns: FilterColumnList, scopeFilter?: ScopeFilter): void {
    if (!scopeFilter || scopeFilter.operator === 'all') { return; }

    const filter = new FilterColumnModel();
    if (scopeFilter.operator === 'null') {
      filter.mapData({
        name: scopeFilter.scope === 'supplier' ? 'SupplierId' : 'LocationId',
        code: scopeFilter.scope === 'supplier' ? 'supplier_id' : 'location_id',
        operator: 'null',
        dataType: 'number',
        value: [0],
      });
    } else if (scopeFilter.operator === 'eq') {
      filter.mapData({
        name: scopeFilter.scope === 'supplier' ? 'SupplierId' : 'LocationId',
        code: scopeFilter.scope === 'supplier' ? 'supplier_id' : 'location_id',
        value: [scopeFilter.id],
        operator: scopeFilter.operator,
        dataType: 'number',
      });
    }
    filterColumns.add(filter);
  }
}
